import React, { useMemo } from 'react';
import { flushSync } from 'react-dom';
import {
  Tooltip,
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Input,
  HStack,
  VStack,
  Divider,
  Grid,
  SimpleGrid,
  Select,
  Checkbox,
  Switch,
  IconButton,
  Icon,
  Table,
  Tbody,
  Tr,
  Td,
  Thead,
  Th,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  InputLeftElement,
  InputGroup,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaPencilAlt,
  FaCheck,
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrashAlt,
  FaTimes, // Close Icon
  FaCalendarAlt,
} from 'react-icons/fa';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'; // Using Chakra icons for arrows
import moment from 'moment-timezone';
import mqtt from 'mqtt';
import { GoCheck } from 'react-icons/go';
import { BsDropletFill } from 'react-icons/bs';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { useEffect, useState, useRef, useCallback } from 'react';

import { useUser } from '../../../userContext';
import { notifyApiError } from 'utils/apiFeedback';

// Icons
import { ReactComponent as WaterLimitIcon } from '../../../assets/icons/Water Limit Icon.svg';
import { ReactComponent as TankLevelIcon } from '../../../assets/icons/Tank Icon.svg';
import { ReactComponent as BorewellIconPumpOn } from '../../../assets/icons/Borewell_Icon_Pump_On.svg';
import { ReactComponent as BorewellIconPumpOff } from '../../../assets/icons/Borewell_Icon_Pump_Off.svg';
// import Overview from '../profile';

const createMask = (length, initialValue = '0') => initialValue.repeat(length);
const DAY_MASK_LENGTH = 31;
const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const STATE_STATUS_TIMEOUT_MS = 2 * 60 * 1000;

// Clickable Day Button Component
const CalendarDayButton = ({ day, isSelected, onClick, isDisabled }) => (
  <Button
    size="sm"
    variant={isSelected ? 'solid' : 'outline'}
    colorScheme={isSelected ? 'blue' : 'gray'}
    onClick={() => onClick(day)}
    w="30px"
    h="30px"
    p={0}
    isDisabled={isDisabled}
  >
    {day}
  </Button>
);

// Dynamic Calendar Grid Component
// Dynamic Calendar Grid Component (Fixed Month View)
const CalendarGrid = ({ daysMask, onDayToggle, currentViewDate }) => {
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const DAY_MASK_LENGTH = 31;
  const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const currentMonth = currentViewDate.getMonth();
  const currentYear = currentViewDate.getFullYear();

  // Calculate day alignment and actual month length dynamically
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay(); // 0=Sunday, 6=Saturday
  const daysInActualMonth = new Date(
    currentYear,
    currentMonth + 1,
    0,
  ).getDate();

  // Array for the 31 visible day numbers (1 to 31)
  const days = Array.from({ length: DAY_MASK_LENGTH }, (_, i) => i + 1);

  // Blank spacers for alignment
  const blanks = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const monthName = currentViewDate.toLocaleString('default', {
    month: 'long',
  });

  return (
    <VStack spacing={2} maxW="300px" p={2}>
      {/* Fixed Month Header (Navigation Removed) */}
      <Flex justify="center" align="center" w="full" px={2}>
        <Heading size="md" fontWeight="semibold">
          {monthName} {currentYear}
        </Heading>
      </Flex>

      {/* Weekday Header */}
      <SimpleGrid columns={7} spacing={1} w="full">
        {WEEKDAYS.map((day) => (
          <Text
            key={day}
            fontSize="xs"
            color={mutedTextColor}
            textAlign="center"
            fontWeight="bold"
          >
            {day.substring(0, 3)}
          </Text>
        ))}
      </SimpleGrid>

      {/* Days Grid */}
      <SimpleGrid columns={7} spacing={1} w="full">
        {/* Render Blanks to offset Day 1 */}
        {blanks.map((_, i) => (
          <Box key={`blank-${i}`} h="30px" />
        ))}

        {/* Render Days 1 through 31 */}
        {days.map((day) => {
          // Disable days beyond the actual month length
          const isDisabled = day > daysInActualMonth;
          // Check if the character at the (day - 1) index is '1' in the 31-char mask
          const isSelected = daysMask.charAt(day - 1) === '1';

          return (
            <CalendarDayButton
              key={day}
              day={day}
              isSelected={isSelected}
              onClick={onDayToggle}
              isDisabled={isDisabled} // Disable/gray out non-existent days
            />
          );
        })}
      </SimpleGrid>
    </VStack>
  );
};

// -----------------------------------------------------------
// --- SCHEDULE MODAL COMPONENT (UPDATED) ---
// -----------------------------------------------------------

const ScheduleModal = ({
  isOpen,
  onClose,
  initialData, // ⬅️ Used for pre-filling
  onSave,
  borewell,
  originalIndex, // ⬅️ Used to pass the index back to the parent
}) => {
  const modalBg = useColorModeValue('white', 'dark.800');
  const borderColor = useColorModeValue('gray.100', 'dark.600');
  const textPrimary = useColorModeValue('gray.800', 'gray.100');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const isEditing = originalIndex !== null && originalIndex !== undefined;

  // 1. Initialize states for time inputs using initialData fields: start and stop
  const [startTime, setStartTime] = useState(initialData?.start || '00:00');
  const [stopTime, setStopTime] = useState(initialData?.stop || '00:00');

  // 2. Initialize state for the 31-day mask using initialData field: days
  const initialMask =
    initialData?.days && initialData.days.length === DAY_MASK_LENGTH
      ? initialData.days
      : createMask(DAY_MASK_LENGTH);
  const [daysMask, setDaysMask] = useState(initialMask);

  // 3. Dynamic Calendar State: Starts at the current date
  const [currentViewDate, setCurrentViewDate] = useState(new Date());

  // Function to change the month being viewed
  const handleMonthChange = (direction) => {
    setCurrentViewDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + direction);
      return newDate;
    });
  };

  // Function to toggle a specific day in the mask string
  const handleDayToggle = useCallback((dayNumber) => {
    // Index is day number - 1
    const index = dayNumber - 1;

    setDaysMask((prevMask) => {
      if (index < 0 || index >= DAY_MASK_LENGTH) return prevMask;

      // Toggle '0' to '1' or '1' to '0'
      const newChar = prevMask.charAt(index) === '0' ? '1' : '0';

      // Construct the new mask string immutably
      return (
        prevMask.substring(0, index) + newChar + prevMask.substring(index + 1)
      );
    });
  }, []);

  // Display selected days (e.g., "1, 2, 5") for the input field
  const selectedDaysDisplay = useMemo(() => {
    const selected = [];
    for (let i = 0; i < DAY_MASK_LENGTH; i++) {
      if (daysMask.charAt(i) === '1') {
        selected.push(i + 1);
      }
    }
    return selected.join(', ');
  }, [daysMask]);

  const handleSubmit = () => {
    // Determine the actual number of days in the current month view
    const currentMonth = currentViewDate.getMonth();
    const currentYear = currentViewDate.getFullYear();
    const daysInActualMonth = new Date(
      currentYear,
      currentMonth + 1,
      0,
    ).getDate(); // e.g., 30 for November

    let finalDaysMask = daysMask;

    // 1. Force remaining bits in the mask to '0' if the month is shorter than 31 days
    if (daysInActualMonth < DAY_MASK_LENGTH) {
      // Example: If 30 days, we start clearing from index 30 (Day 31)
      const startIndexToClear = daysInActualMonth;

      // Ensure the part corresponding to the actual month is preserved
      const preservedPart = daysMask.substring(0, startIndexToClear);

      // Create the clearing part (e.g., '0' for November, '000' for February 28)
      const clearedPart = createMask(DAY_MASK_LENGTH - startIndexToClear);

      finalDaysMask = preservedPart + clearedPart;
    }

    // 2. Create the final schedule object
    const newScheduleData = {
      status: '1',
      start: startTime,
      stop: stopTime,
      days: finalDaysMask, // ⬅️ Use the constrained mask
    };

    // 3. Pass the new data and the array index back to the parent
    onSave(newScheduleData, originalIndex);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg} color={textPrimary} borderRadius="lg" pb={4}>
        <ModalHeader
          borderBottom="1px solid"
          borderColor={borderColor}
          py={4}
          px={6}
        >
          <Text fontSize="sm" color={textMuted}>
            {borewell}
          </Text>
          <Heading size="lg" fontWeight="semibold">
            {isEditing ? 'Edit Schedule' : 'Add Schedule'}
          </Heading>
          <IconButton
            icon={<FaTimes />}
            variant="ghost"
            size="sm"
            position="absolute"
            right="8px"
            top="8px"
            onClick={onClose}
            aria-label="Close"
          />
        </ModalHeader>

        <ModalBody p={6}>
          {/* TIME INPUTS */}
          <SimpleGrid columns={2} spacing={4} mb={6}>
            <VStack align="stretch">
              <Text fontSize="md" fontWeight="medium" color={textPrimary}>
                Start Time
              </Text>
              <Input
                type="time"
                size="lg"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="00:00"
              />
            </VStack>
            <VStack align="stretch">
              <Text fontSize="md" fontWeight="medium" color={textPrimary}>
                Stop Time
              </Text>
              <Input
                type="time"
                size="lg"
                value={stopTime}
                onChange={(e) => setStopTime(e.target.value)}
                placeholder="00:00"
              />
            </VStack>
          </SimpleGrid>

          {/* CALENDAR INPUT & GRID */}
          <VStack align="stretch" spacing={4}>
            <Text fontSize="md" fontWeight="medium" color={textPrimary}>
              Select Days (Current Month)
            </Text>
            <Box position="relative">
              <Input
                size="lg"
                readOnly
                placeholder="Select days..."
                value={selectedDaysDisplay || ''}
                pr="4.5rem"
              />
              <IconButton
                icon={<Box as={FaTimes} visibility="hidden" />} // Placeholder icon to match image layout
                size="md"
                variant="ghost"
                position="absolute"
                right="4px"
                top="50%"
                transform="translateY(-50%)"
                aria-label="Calendar Icon"
              />
            </Box>

            <Flex justify="center" pt={2}>
              <CalendarGrid
                daysMask={daysMask}
                onDayToggle={handleDayToggle}
                currentViewDate={currentViewDate}
                // onMonthChange={handleMonthChange}
              />
            </Flex>
          </VStack>
        </ModalBody>

        <ModalFooter justifyContent="center" pt={4}>
          <Button
            bg="rgba(0, 106, 184, 1)"
            color="white"
            rounded="base"
            size="lg"
            w="full"
            fontWeight="semibold"
            onClick={handleSubmit}
          >
            Save Schedule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const formatScheduleDays = (daysMask) => {
  if (!daysMask || daysMask.length !== 31) {
    return 'N/A';
  }

  // Use current date to determine the month context
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 9 = October

  const output = [];

  // Helper to get month name (e.g., 'Oct')
  const getMonthName = (monthIndex) => {
    const date = new Date(currentYear, monthIndex, 1);
    return date.toLocaleString('default', { month: 'short' });
  };

  // Helper to get days in a given month
  const getDaysInMonth = (monthIndex) => {
    return new Date(currentYear, monthIndex + 1, 0).getDate();
  };

  // --- Process Current Month (e.g., October) ---
  const daysInCurrentMonth = getDaysInMonth(currentMonth);
  const currentMonthDays = [];

  for (let i = 0; i < daysInCurrentMonth; i++) {
    if (daysMask.charAt(i) === '1') {
      // Day number (1-based index)
      currentMonthDays.push((i + 1).toString().padStart(2, '0'));
    }
  }

  if (currentMonthDays.length > 0) {
    output.push(
      `${getMonthName(currentMonth)} ${currentYear}(${currentMonthDays.join(
        ', ',
      )})`,
    );
  }

  return output.join(', '); // If you need to handle month wraps, this logic must be adjusted.
};

// --- UTILITY FUNCTION (Place this in your parent component or a helpers file) ---

const formatTime12Hour = (time24h) => {
  if (!time24h) return 'N/A';

  // Parse the H:MM string
  const [hours, minutes] = time24h.split(':').map(Number);

  // Determine AM/PM suffix
  const suffix = hours >= 12 ? 'PM' : 'AM';

  // Convert 24-hour format to 12-hour format
  const hours12 = hours % 12 || 12; // Converts 0 to 12 (midnight) and 12 to 12 (noon)

  // Format minutes with leading zero if necessary
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${hours12}:${formattedMinutes} ${suffix}`;
};

// --- SUPPORT COMPONENTS (Simplified from previous response) ---

const BorewellCard = ({
  title,
  statusColor = 'red.500',
  isPumpActive = false,
}) => {
  const cardBg = useColorModeValue('white', 'dark.800');
  const iconBg = useColorModeValue('blue.50', 'dark.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const borderColor = useColorModeValue('gray.100', 'dark.600');
  const PumpIcon = isPumpActive ? BorewellIconPumpOn : BorewellIconPumpOff;

  return (
    <Flex
      p={4}
      bg={cardBg}
      borderRadius="md"
      boxShadow="sm"
      align="center"
      justify="space-between"
      border="1px solid"
      borderColor={borderColor}
      borderLeftWidth="6px"
      borderLeftStyle="solid"
      borderLeftColor={statusColor}
    >
      <HStack spacing={4}>
        <Flex
          w="45px"
          h="45px"
          borderRadius="full"
          bg={iconBg}
          border="2px solid"
          borderColor={statusColor}
          align="center"
          justify="center"
        >
          <PumpIcon />
        </Flex>
        <Text fontWeight="medium" color={textColor}>
          {title}
        </Text>
      </HStack>
    </Flex>
  );
};

// --- MAIN DASHBOARD COMPONENT ---

const DashboardUI = () => {
  const { updateLicenseData } = useUser();
  const [overViewData, setOverViewData] = useState([]);
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  // State to hold the specific schedule item and its index being edited
  // We need this state to display the modal only for editing
  const [scheduleToEdit, setScheduleToEdit] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const {
    isOpen: isConfirmationOpen,
    onOpen: onConfirmationOpen,
    onClose: onConfirmationClose,
  } = useDisclosure();

  // const [currentSchedule, setCurrentSchedule] = useState(null);
  const [pendingPacket, setPendingPacket] = useState(null);
  const [pendingTopic, setPendingTopic] = useState(null);

  const [selectedBorewell, setselectedBorewell] = useState(null);
  const [selectedPID, setselectedPID] = useState(null);
  const pendingPIDRef = useRef(null);
  const [companyData, setCompanyData] = useState([]);

  const [limitPerDay, setLimitPerDay] = useState(0);
  const [limitPerYear, setlimitPerYear] = useState(0);
  const [companyId, setCompanyId] = useState(0);

  const [WaterTankLevel, setWaterTankLevel] = useState(0);
  const [plantDailyCons, setPlantDailyCons] = useState(0);
  const [plantYearlyCons, setPlantYearlyCons] = useState(0);

  const [newLimit, setNewLimit] = useState();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [quantityBasedLimit, setQuantityBasedLimit] = useState();

  const ACK_TIMEOUT = 15;
  const publishingRef = useRef(false);
  const ackReceivedRef = useRef(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const clientRef = useRef(null);
  const messageHandlerRef = useRef(null);
  const stateLastSeenRef = useRef({});
  const stateStatusTimeoutsRef = useRef({});
  const cancelRef = useRef();

  const selectedCompany = useRef('');

  const deviceAccess = useRef();

  const [deviceConf, setDeviceConf] = useState({
    pid: '',
    mid: '',
    timestamp: '',
    schedules: {
      status: '',
      cfg: [],
    },
    level: {
      status: '',
      thigh: '',
      tlow: '',
    },
    utilization: {
      status: '',
      limit: '',
    },
  });

  const [deviceStatus, setDeviceStatus] = useState('');

  const toast = useToast();
  const Token = localStorage.getItem('token');
  const userRole = localStorage.getItem('role');
  const pageBg = useColorModeValue('gray.50', 'dark.900');
  const surfaceBg = useColorModeValue('white', 'dark.800');
  const insetBg = useColorModeValue('gray.50', 'dark.700');
  const borderColor = useColorModeValue('gray.200', 'dark.600');
  const textPrimary = useColorModeValue('gray.800', 'gray.100');
  const textMuted = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('rgba(0, 106, 184, 1)', 'aqua.300');
  const accentStrongColor = useColorModeValue(
    'rgba(17, 128, 211, 1)',
    'aqua.300',
  );
  const shadowValue = useColorModeValue(
    'md',
    '0 18px 48px rgba(0, 0, 0, 0.24)',
  );
  const parseMqttJson = useCallback((message, context) => {
    const payload = message?.toString?.() ?? '';

    try {
      return JSON.parse(payload);
    } catch (error) {
      console.warn(
        `Ignored malformed MQTT JSON packet${context ? ` (${context})` : ''}:`,
        {
          error: error.message,
          payload,
        },
      );
      return null;
    }
  }, []);

  const getPacketSeenTime = (item) => {
    const timestamp = new Date(item?.updatedAt || item?.timestamp).getTime();

    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const normalizeDeviceStatus = (item, now = Date.now()) => {
    const lastSeenAt =
      stateLastSeenRef.current[item?.productId] || getPacketSeenTime(item);

    return lastSeenAt && now - lastSeenAt <= STATE_STATUS_TIMEOUT_MS
      ? 'online'
      : 'offline';
  };

  const normalizeOverviewStatuses = (data = []) => {
    const now = Date.now();

    return (data || []).map((item) => ({
      ...item,
      status: normalizeDeviceStatus(item, now),
    }));
  };

  const isBorewellRunning = (item) =>
    item?.active !== false &&
    item?.status === 'online' &&
    item?.system?.pump_status === 'on';

  const getBorewellStatusColor = (item) =>
    isBorewellRunning(item) ? 'green.500' : 'red.500';

  // Local Broker
  // const brokerUrl = 'ws://localhost:8082';
  // const username = '';
  // const password = '';

  // Staging Broker
  // const brokerUrl = 'wss://atbcmqtt.aquasense.tech:9001';
  // const username = 'admin';
  // const password = 'fVAYl6k2Kd9Z';

  // Production Broker
  const brokerUrl = 'wss://atbc.aquasense.tech:9001';
  const username = 'atbcmqtt';
  const password = 'atbc@mqtt1911';

  // --- Simplified Handlers ---

  // 1. Function to open the ADD modal
  const handleOpenAddModal = () => {
    setScheduleToEdit(null); // Ensure no old data is present
    setEditIndex(null); // Ensure no index is present
    onAddOpen(); // Open the dedicated ADD modal
  };

  // 2. Function to open the EDIT modal
  const handleOpenEditModal = (index) => {
    const itemToEdit = deviceConf?.schedules?.cfg[index];

    setScheduleToEdit(itemToEdit);
    setEditIndex(index);
    onEditOpen(); // Open the dedicated EDIT modal
  };

  // 3. Function to handle Deletion (cleanest function)
  const handleDeleteSchedule = (indexToDelete) => {
    setDeviceConf((prevConf) => ({
      ...prevConf,
      schedules: {
        ...prevConf.schedules,
        cfg: prevConf.schedules.cfg.filter(
          (_, index) => index !== indexToDelete,
        ),
      },
    }));
  };

  const getQueryString = (params) => {
    return Object.keys(params)
      .filter((key) => params[key] !== undefined && params[key] !== '')
      .map((key) => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');
  };

  // Function called by both ADD and EDIT modals
  const handleSaveSchedule = (newScheduleData, indexToUpdate) => {
    setDeviceConf((prevConf) => {
      const currentCfg = prevConf.schedules.cfg || [];

      // 🧩 Check for max limit when adding a new schedule
      if (indexToUpdate === null && currentCfg.length >= 5) {
        toast({
          title: 'Limit Reached',
          description: 'You can only add up to 5 schedule configurations.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return prevConf; // No change
      }

      let newCfg;

      if (indexToUpdate !== null) {
        // ✏️ EDIT logic
        newCfg = currentCfg.map((item, index) =>
          index === indexToUpdate ? { ...item, ...newScheduleData } : item,
        );
      } else {
        // ➕ ADD logic
        newCfg = [...currentCfg, newScheduleData];
      }

      return {
        ...prevConf,
        schedules: {
          ...prevConf.schedules,
          cfg: newCfg,
        },
      };
    });
  };

  // const handleDelete = (id) => {
  //   setScheduleData(scheduleData.filter((row) => row.id !== id));
  // };

  const plantOverviewData = (data) => {
    let plantDailyConsumption = 0;
    let plantYearlyConsumption = 0;
    let plantTankLevel = 0;

    if (!Array.isArray(data) || data.length === 0) {
      setWaterTankLevel(0);
      setPlantDailyCons('0.00');
      setPlantYearlyCons('0.00');
      return;
    }

    for (let item of data) {
      const dailyConsumption =
        item.dailyConsumption !== '-' &&
        item.dailyConsumption !== null &&
        item.dailyConsumption !== ''
          ? parseFloat(item.dailyConsumption)
          : 0;
      const yearlyConsumption =
        item.yearlyConsumption !== '-' &&
        item.yearlyConsumption !== null &&
        item.yearlyConsumption !== ''
          ? parseFloat(item.yearlyConsumption)
          : 0;
      const water_level =
        item.tank_sensor.water_level !== '-' &&
        item.tank_sensor.water_level !== null
          ? parseFloat(item.tank_sensor.water_level)
          : 0;
      plantDailyConsumption += dailyConsumption;
      plantYearlyConsumption += yearlyConsumption;
      plantTankLevel += water_level;
    }

    setWaterTankLevel(plantTankLevel / data.length);
    setPlantDailyCons(plantDailyConsumption.toFixed(2));
    setPlantYearlyCons(plantYearlyConsumption.toFixed(2));
  };

  const handleLimitChange = (pid, value) => {
    value = Number(value) || 0;
    setOverViewData((prevLimits) =>
      prevLimits.map((item) =>
        item.productId === pid ? { ...item, limitPerDay: value } : item,
      ),
    );
  };

  const getLimitFromDevice = (pid) => {
    if (pid) {
      const limitQuantity = overViewData.find((item) => item.productId === pid);
      setQuantityBasedLimit(limitQuantity.limitPerDay);
    }
  };

  const checkUserAccess = (pid) => {
    if (pid && pid !== '') {
      const selectedDevice = overViewData.filter(
        (item) => item.productId === pid,
      );
      const control = selectedDevice?.[0]?.devicesAccess?.[0]?.control;
      const canControl = userRole !== 'user' || control === true;
      deviceAccess.current = !canControl;
      if (!canControl) {
        toast({
          title: `Access to device ${pid} has been denied.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const sendUpdatedLimits = async () => {
    const checkForExceedingLimit = calculateRemaingLimit();
    if (checkForExceedingLimit === 'Exceeds') {
      toast({
        title: 'Total limit exceeds.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const requestData = [];
    for (let item of overViewData) {
      requestData.push({
        pid: item.productId,
        limitPerDay: item.limitPerDay,
      });
    }
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/device/devicePermissibleLimits`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response.data.status) {
        toast({
          title: 'Limit updated successfully.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        fetchOverviewData();
        getLimitFromDevice(selectedPID);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load automation data');
    }
  };

  const calculateRemaingLimit = () => {
    const allInputLimits = overViewData.reduce(
      (sum, item) => sum + Number(item.limitPerDay),
      0,
    );
    const remLimit = limitPerDay - allInputLimits;
    return remLimit >= 0 ? remLimit : 'Exceeds';
  };

  const fetchPermissibleLimits = async () => {
    try {
      let response;
      if (['companyUser', 'user'].includes(userRole)) {
        response = await axiosInstance.get(
          `${baseUrl}/company/getPermissibleLimits`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      } else if (userRole === 'admin') {
        if (!selectedCompany.current || selectedCompany.current === '') {
          toast({
            title: 'Please select a valid company',
            status: 'error',
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        response = await axiosInstance.get(
          `${baseUrl}/company/getPermissibleLimits?companyId=${selectedCompany.current}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      }
      if (response) {
        const data = response?.data?.data;
        setLimitPerDay(data[0].limitPerDay);
        setlimitPerYear(data[0].limitPerYear);
        updateLicenseData({
          nocNumber: data[0]?.nocDetails?.nocNumber,
          limitPerDay: data[0].limitPerDay,
          limitPerYear: data[0].limitPerYear,
          nocStartDate: data[0]?.nocDetails?.nocExpiry?.startDate,
          nocEndDate: data[0]?.nocDetails?.nocExpiry?.endDate,
          companyLogoUrl: data[0]?.companyLogoUrl,
        });
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load automation data');
    }
  };

  const fetchOverviewData = async () => {
    try {
      let response;
      if (['companyUser', 'user'].includes(userRole)) {
        response = await axiosInstance.get(
          `${baseUrl}/device-readings/fetchOverviewData`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      } else if (userRole === 'admin') {
        if (!selectedCompany.current || selectedCompany.current === '') {
          toast({
            title: 'Please select a valid company',
            status: 'error',
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        response = await axiosInstance.get(
          `${baseUrl}/device-readings/fetchOverviewData?companyId=${selectedCompany.current}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      }
      if (response) {
        const data = response?.data?.data?.data;
        const overviewData = normalizeOverviewStatuses(data || []);
        plantOverviewData(overviewData);
        setOverViewData(overviewData);
        updateLicenseData({
          borewellCount: overviewData.length,
        });
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load automation data');
    }
  };

  const fetchDeviceConfs = async (productId) => {
    pendingPIDRef.current = productId;
    const selectedItem = overViewData.find(
      (item) => item.productId === productId,
    );

    if (selectedItem) {
      const displayString = `${selectedItem.borewellName} (${selectedItem.productId})`;
      setselectedBorewell(displayString);
    } else {
      setselectedBorewell('');
    }
    try {
      const queryParams = {};
      if (productId) {
        queryParams.productId = productId;
      } else {
        toast({
          title: 'Please select a valid borewell',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const response = await axiosInstance.get(
        `${baseUrl}/device/deviceConfs?${getQueryString(queryParams)}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        const data = response?.data?.data?.data;
        if (data && Object.keys(data).length !== 0) {
          setDeviceConf(data);
        } else {
          setDeviceConf({
            pid: '',
            mid: '',
            timestamp: '',
            schedules: {
              status: '',
              cfg: [],
            },
            level: {
              status: '',
              thigh: '',
              tlow: '',
            },
            utilization: {
              status: '',
              limit: '',
            },
          });
          toast({
            title: 'Configuration has not been set for this device',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error(error);
      notifyApiError(error, toast, 'Something went wrong');
    }
  };

  const checkValidity = (licenseExpiry) => {
    const expiryDate = moment(licenseExpiry.split('T')[0]).toDate();
    const currDate = new Date();
    if (currDate >= expiryDate) {
      return true;
    } else {
      return false;
    }
  };

  const updateQuantityConf = (newLimit) => {
    setDeviceConf((prevConf) => ({
      ...prevConf,
      utilization: {
        ...prevConf.utilization,
        limit: newLimit,
      },
    }));
  };

  const updateLevelConf = (newLimit) => {
    const keyToUpdate = newLimit[0] === 'start' ? 'tlow' : 'thigh';
    const newValue = newLimit[1];

    setDeviceConf((prevConf) => ({
      ...prevConf,
      level: {
        ...prevConf.level,
        [keyToUpdate]: newValue,
      },
    }));
  };

  const handleCancel = () => {
    onConfirmationClose();
    toast({
      title: 'Command cancelled',
      status: 'info',
      duration: 2000,
    });
  };

  const confirmAndSend = (packet, topic) => {
    // store packet/topic temporarily until user confirms
    setPendingPacket(packet);
    setPendingTopic(topic);
    onConfirmationOpen();
  };

  const toggleQuantityBasedStatus = (isChecked) => {
    const newStatus = isChecked ? '1' : '0';

    setDeviceConf((prevConf) => {
      return {
        ...prevConf,
        utilization: {
          ...prevConf.utilization,
          status: newStatus,
        },
      };
    });
  };

  const toggleLevelBasedStatus = (isChecked) => {
    const newStatus = isChecked ? '1' : '0';

    setDeviceConf((prevConf) => {
      return {
        ...prevConf,
        level: {
          ...prevConf.level,
          status: newStatus,
        },
      };
    });
  };

  const toggleScheduleBasedStatus = (isChecked) => {
    const newStatus = isChecked ? '1' : '0';

    setDeviceConf((prevConf) => {
      return {
        ...prevConf,
        schedules: {
          ...prevConf.schedules,
          status: newStatus,
        },
      };
    });
  };

  // --- Inside your Parent Component (where deviceConf state is managed) ---

  const handleToggleStatus = (indexToUpdate, isChecked) => {
    // Determine the new status value ('1' or '0')
    const newStatus = isChecked ? '1' : '0';

    setDeviceConf((prevConf) => {
      // Create a deep copy of the schedules array
      const updatedCfg = prevConf.schedules.cfg.map((item, index) => {
        if (index === indexToUpdate) {
          // Return a new object for the item being updated
          return {
            ...item,
            status: newStatus,
          };
        }
        // Return the original item for others
        return item;
      });

      // Return the new deviceConf state object
      return {
        ...prevConf,
        schedules: {
          ...prevConf.schedules,
          cfg: updatedCfg,
        },
      };
    });
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/company/list`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      if (response) {
        const data = response?.data?.data?.data;
        setCompanyData(data);
        if (!data || data.length <= 0) {
          toast({
            title: 'No companies found',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load automation data');
    }
  };

  const handleConfirm = () => {
    onConfirmationClose();

    const packet = pendingPacket;
    const topic = pendingTopic;

    if (publishingRef.current) {
      toast({
        title: 'Please wait for the previous operation to complete',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!clientRef.current) {
      toast({
        title: 'MQTT client is not connected',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    publishingRef.current = true;

    clientRef.current.publish(topic, packet, { qos: 2 }, (err) => {
      if (err) {
        publishingRef.current = false;
        toast({
          title: 'Failed to publish packet',
          status: 'error',
          duration: 2000,
        });
        return;
      }

      // Success toast
      toast({
        title: 'Packet sent successfully!',
        status: 'success',
        duration: 2000,
      });

      // ---- FORCE CLEANUP ANY OLD COUNTS / TOASTS ----
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      toast.close('ack-countdown'); // remove any stuck toast
      ackReceivedRef.current = false;

      // ---- START NEW COUNTDOWN ----
      let countdown = ACK_TIMEOUT;

      toast({
        id: 'ack-countdown',
        title: `Waiting for device acknowledgment... ${countdown}s`,
        status: 'info',
        duration: null,
        isClosable: false,
      });

      intervalRef.current = setInterval(() => {
        countdown -= 1;

        if (countdown <= 0) return;

        try {
          toast.update('ack-countdown', {
            title: `Waiting for device acknowledgment... ${countdown}s`,
            status: 'info',
            duration: null,
            isClosable: false,
          });
        } catch (e) {
          console.warn('Toast update failed, stopping countdown:', e);
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        if (!ackReceivedRef.current) {
          // stop countdown
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          // force close the stuck toast
          toast.close('ack-countdown');

          publishingRef.current = false;

          toast({
            title: 'Device did not respond, rolling back to previous state',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      }, ACK_TIMEOUT * 1000);
    });
  };

  const formatTimestamp = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const day = date.getDate().toString().padStart(2, '0');

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleUpdate = async () => {
    if (newLimit === limitPerDay) {
      setIsEditing(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.put(
        `/company/getPermissibleLimits?companyId=${companyId}&limitPerDay=${newLimit}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );

      if (res.data.status) {
        fetchPermissibleLimits();
        toast({
          title: 'Limit updated successfully.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: res.data.data || 'Failed to update limit.',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      notifyApiError(error, toast, 'Error updating limit.');
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const generateMid = () =>
    Math.floor(100000000 + Math.random() * 900000000).toString();

  const sendConfs = () => {
    const selectedDevice = overViewData.find(
      (item) => item.productId === selectedPID,
    );
    const selectedDeviceStatus = selectedDevice?.status || deviceStatus;

    if (selectedDeviceStatus === 'offline') {
      toast({
        title: 'The device is currently offline',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const { level } = deviceConf || {};
    if (level?.status === '1') {
      if (level && Number(level.tlow) > Number(level.thigh)) {
        toast({
          title: 'Invalid Configuration',
          description: "'Low level' cannot be greater than 'High level'.",
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // 1. Get the current date and format it
    const currentTimestamp = formatTimestamp(new Date());
    const mid = generateMid();
    const updatedDeviceConf = {
      ...deviceConf,
      pid: selectedPID,
      timestamp: currentTimestamp,
      mid: mid,
      utilization: {
        ...deviceConf.utilization,
        limit: String(quantityBasedLimit),
      },
    };

    const jsonParsed = JSON.stringify(updatedDeviceConf);
    confirmAndSend(jsonParsed, 'ATBC/C2D');
  };

  useEffect(() => {
    if (userRole === 'admin') {
      fetchCompanies();
    }
  }, []);

  useEffect(() => {
    if (['companyUser', 'user'].includes(userRole)) {
      fetchOverviewData();
    }
  }, []);

  useEffect(() => {
    if (['companyUser', 'user'].includes(userRole)) {
      fetchPermissibleLimits();
    }
  }, []);

  const scheduleStateStatusTimeout = useCallback((productId) => {
    if (!productId) return;

    if (stateStatusTimeoutsRef.current[productId]) {
      clearTimeout(stateStatusTimeoutsRef.current[productId]);
    }

    stateStatusTimeoutsRef.current[productId] = setTimeout(() => {
      setOverViewData((prevData) =>
        prevData.map((item) =>
          item.productId === productId ? { ...item, status: 'offline' } : item,
        ),
      );
      delete stateStatusTimeoutsRef.current[productId];
    }, STATE_STATUS_TIMEOUT_MS);
  }, []);

  const onStateData = useCallback(
    (message) => {
      const recPacket = parseMqttJson(message, 'ATBC/STATE');
      const productId = recPacket?.pid;

      if (!productId) return;

      const datetime = recPacket.timestamp
        ? moment(recPacket.timestamp, 'YYYY-MM-DD HH:mm:ss').toISOString()
        : new Date().toISOString();

      stateLastSeenRef.current[productId] = Date.now();
      scheduleStateStatusTimeout(productId);

      if (selectedPID === productId) {
        setDeviceStatus('online');
      }

      setOverViewData((prevData) =>
        prevData.map((item) => {
          if (item.productId !== productId) return item;

          return {
            ...item,
            status: 'online',
            updatedAt: datetime,
            timestamp: datetime,
            system: recPacket.system || item.system,
            flowmeter_sensor: recPacket.flowmeter_sensor
              ? {
                  ...item.flowmeter_sensor,
                  ...recPacket.flowmeter_sensor,
                }
              : item.flowmeter_sensor,
            tank_sensor: recPacket.tank_sensor
              ? {
                  ...item.tank_sensor,
                  ...recPacket.tank_sensor,
                }
              : item.tank_sensor,
            borewell_sensor: recPacket.borewell_sensor
              ? {
                  ...item.borewell_sensor,
                  ...recPacket.borewell_sensor,
                }
              : item.borewell_sensor,
          };
        }),
      );
    },
    [parseMqttJson, scheduleStateStatusTimeout, selectedPID],
  );

  useEffect(() => {
    const client = mqtt.connect(brokerUrl, {
      username: username,
      password: password,
      clean: true, // start a clean session
      reconnectPeriod: 1000,
    });
    clientRef.current = client;
    const stateStatusTimeouts = stateStatusTimeoutsRef.current;

    const onAck = async (msgTopic, message) => {
      if (msgTopic === 'ATBC/D2C') {
        const recPacket = parseMqttJson(message, msgTopic);
        if (!recPacket?.pid) return;

        if (
          ('level' in recPacket ||
            'schedules' in recPacket ||
            'utilization' in recPacket) &&
          pendingPIDRef.current === recPacket.pid
        ) {
          if (recPacket['result'] === 'pass') {
            toast({
              title: 'Device acknowledged configurations command',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            delete recPacket['result'];
            setDeviceConf(recPacket);

            try {
              await axiosInstance.put(
                `${baseUrl}/device/deviceConfs?productId=${recPacket.pid}`,
                recPacket,
                {
                  headers: {
                    Authorization: `Bearer ${Token}`,
                  },
                },
              );
            } catch (error) {
              notifyApiError(error, toast, 'Something went wrong');
            }
          } else {
            fetchDeviceConfs(selectedBorewell);
            toast({
              title: 'Device configuration failed',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            toast({
              title: 'Getting back to last state',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        }

        ackReceivedRef.current = true;
        publishingRef.current = false;
        clearInterval(intervalRef.current);
        clearTimeout(timeoutRef.current);
        toast.close('ack-countdown');
      }
    };

    const onDeviceStatus = (msgTopic, message) => {
      const decodedPacket = message.toString().split(',');
      const productId = decodedPacket[0];
      const currStatus = decodedPacket[1]?.trim()?.toLowerCase();

      if (currStatus === 'offline') {
        delete stateLastSeenRef.current[productId];
        if (stateStatusTimeoutsRef.current[productId]) {
          clearTimeout(stateStatusTimeoutsRef.current[productId]);
          delete stateStatusTimeoutsRef.current[productId];
        }
      }

      if (currStatus === 'online' || currStatus === 'offline') {
        setOverViewData((prevData) =>
          prevData.map((item) =>
            item.productId === productId
              ? { ...item, status: currStatus }
              : item,
          ),
        );
      }

      if (selectedPID === productId) {
        setDeviceStatus(currStatus);
      }
    };

    const handleMessage = (topic, message) => {
      const t = topic.toString().trim(); // ensure it's a clean string

      if (t === 'ATBC/D2C') {
        onAck(t, message);
      } else if (t === 'ATBC/STATE') {
        onStateData(message);
      } else if (t.startsWith('ATBC/STATUS/')) {
        setTimeout(() => {
          onDeviceStatus(t, message);
        }, 1000);
      }
    };

    messageHandlerRef.current = handleMessage;
    client.on('message', handleMessage);

    client.on('connect', () => {
      console.log('Connected to broker');
      client.subscribe(['ATBC/D2C', 'ATBC/STATUS/+', 'ATBC/STATE'], (err) => {
        if (err) console.error('Subscription failed:', err);
      });
    });

    return () => {
      if (messageHandlerRef.current) {
        client.off('message', messageHandlerRef.current);
      }
      client.unsubscribe(['ATBC/D2C', 'ATBC/STATUS/+', 'ATBC/STATE']);
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
      Object.values(stateStatusTimeouts).forEach(clearTimeout);
      client.end(true);
    };
  }, [brokerUrl, onStateData, parseMqttJson, selectedPID]);

  return (
    <Grid
      templateColumns={{ base: '1fr', lg: '3fr 1.5fr' }}
      gap={6}
      p={6}
      bg={pageBg}
      color={textPrimary}
      minH="100vh"
      mt="65px"
    >
      {/* PACKET CONFIRMATION MODAL */}
      <AlertDialog
        isOpen={isConfirmationOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmationClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Publish
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you certain you want to trigger the command?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancel}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={() => handleConfirm()} ml={3}>
                Okay
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 1. ADD SCHEDULE MODAL */}
      <ScheduleModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSave={(newScheduleData) => handleSaveSchedule(newScheduleData, null)} // Pass null index for ADD
        schedule={null}
        originalIndex={null}
        borewell={selectedBorewell}
      />

      {/* 1. Edit SCHEDULE MODAL */}
      {scheduleToEdit && ( // Render only when an item is selected for editing
        <ScheduleModal
          isOpen={isEditOpen}
          onClose={onEditClose}
          onSave={(newScheduleData) =>
            handleSaveSchedule(newScheduleData, editIndex)
          } // The index is correctly passed here.
          // REPAIR: Use initialData prop name to match modal's internal logic
          initialData={scheduleToEdit}
          originalIndex={editIndex}
          borewell={selectedBorewell}
        />
      )}

      {/* --- LEFT CONTENT AREA --- */}
      <VStack spacing={6} align="stretch">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '1.25rem',
          }}
        >
          <Heading size="md" fontWeight="semibold" color={textPrimary}>
            Define Abstraction Limit
          </Heading>
          {userRole === 'admin' && (
            <Select
              placeholder="Select Company"
              size="md"
              flex="1"
              maxW="210px"
              onChange={(e) => {
                selectedCompany.current = e.target.value;
                fetchOverviewData();
                fetchPermissibleLimits();
              }}
            >
              {/* Dynamic rendering of options */}
              {companyData.map((item) => (
                <option key={item.productId} value={item._id}>
                  {item.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Box
          p={6}
          bg={surfaceBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow={shadowValue}
        >
          <HStack mb={4} align="center">
            <Heading size="sm" fontWeight="medium">
              Permissible Per Day Limit (Plant) (KLD)
            </Heading>
            {!isEditing ? (
              <>
                <Text ml={1} fontWeight="bold" color={accentColor}>
                  {limitPerDay}
                </Text>
                <Icon
                  as={FaPencilAlt}
                  w={3}
                  h={3}
                  color={userRole !== 'companyUser' ? 'gray.300' : 'gray.600'}
                  cursor={
                    userRole !== 'companyUser' ? 'not-allowed' : 'pointer'
                  }
                  onClick={
                    userRole === 'companyUser'
                      ? () => setIsEditing(true)
                      : undefined
                  }
                />
              </>
            ) : (
              <Flex align="center" gap={2}>
                <Input
                  size="sm"
                  width="80px"
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  autoFocus
                />
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <Icon
                    as={FaCheck}
                    w={4}
                    h={4}
                    color="green.500"
                    cursor="pointer"
                    onClick={handleUpdate}
                  />
                )}
              </Flex>
            )}
          </HStack>
          <SimpleGrid columns={4} spacing={4} mb={6}>
            {overViewData.map((item, index) => {
              // 🔵 Access from API
              const isExpired = checkValidity(item.licenseExpiry);
              const monitor = item.devicesAccess?.[0]?.monitor;
              const control = item.devicesAccess?.[0]?.control;

              // 🔵 USER ACCESS RULES
              const canControl =
                (userRole !== 'user' && userRole !== 'admin') ||
                control === true;
              const disableInput = !canControl;

              return (
                <VStack key={index} align="flex-start" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold" color={textMuted}>
                    {item.borewellName} ({item.productId})
                  </Text>
                  <Tooltip
                    label="License has been expired for this device, please renew to continue..."
                    isDisabled={!isExpired}
                    hasArrow
                  >
                    <Box cursor={isExpired ? 'not-allowed' : 'text'}>
                      <InputGroup size="md">
                        <InputLeftElement
                          pointerEvents="none"
                          children={
                            <HStack spacing={2}>
                              <Text fontWeight="medium" color={textPrimary}>
                                KLD
                              </Text>
                              <Text fontWeight="medium" color={textMuted}>
                                |
                              </Text>
                            </HStack>
                          }
                          pl={2}
                          width="50px"
                        />

                        <Input
                          disabled={disableInput}
                          isDisabled={isExpired}
                          opacity={isExpired ? 0.4 : 1}
                          pointerEvents={isExpired ? 'none' : 'auto'}
                          type="number"
                          onChange={(e) =>
                            handleLimitChange(item.productId, e.target.value)
                          }
                          defaultValue={item.limitPerDay}
                          pl="55px"
                        />
                      </InputGroup>
                    </Box>
                  </Tooltip>
                </VStack>
              );
            })}
          </SimpleGrid>
          {overViewData.length > 0 && (
            <Flex justify="space-between" align="center">
              <Button
                onClick={() => sendUpdatedLimits()}
                bg={accentColor}
                color="white"
                fontWeight="semibold"
                px={24}
                rounded="base"
                opacity={userRole !== 'admin' ? 1 : 0.5}
                pointerEvents={userRole !== 'admin' ? 'auto' : 'none'}
              >
                Save
              </Button>
              <Text fontSize="sm" color="red.500" fontWeight="medium">
                {/* Remaining Limit (1KLD) */}
                Remaining Limit {calculateRemaingLimit()}
              </Text>
            </Flex>
          )}
        </Box>
        {/* AUTOMATION SETUP CARD */}
        <VStack
          p={6}
          bg={surfaceBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow={shadowValue}
          align="stretch"
          spacing={6}
        >
          <Heading size="md" fontWeight="semibold">
            Automation Setup
          </Heading>
          <HStack spacing={4} align="center">
            <Text fontSize="sm" fontWeight="medium">
              Select Borewell
            </Text>
            <Select
              placeholder="Select Borewell..." // Changed placeholder for clarity
              size="md"
              flex="1"
              maxW="300px"
              onChange={(e) => {
                fetchDeviceConfs(e.target.value);
                setselectedPID(e.target.value);
                getLimitFromDevice(e.target.value);
                checkUserAccess(e.target.value);
              }}
            >
              {/* <option
                value=""
                disabled // Makes it unselectable
                // hidden // Hides it from the dropdown list after selection (optional, but good practice)
              >
                Select Borewell
              </option> */}

              {/* Dynamic rendering of options */}
              {overViewData.map((item) => {
                const isExpired = checkValidity(item.licenseExpiry);
                return (
                  <option
                    disabled={isExpired}
                    key={item.productId}
                    value={item.productId}
                  >
                    {/* The displayed text format: "Main Gate (ATBC1234)" */}
                    {`${item.borewellName} (${item.productId})`}
                  </option>
                );
              })}
            </Select>
          </HStack>

          <SimpleGrid
            columns={2}
            spacing={6}
            opacity={selectedBorewell ? 1 : 0.5}
            pointerEvents={selectedBorewell ? 'auto' : 'none'}
          >
            {/* Quantity Based Automation */}
            <Box
              p={4}
              border="1px solid"
              borderColor={borderColor}
              bg={insetBg}
              borderRadius="md"
              opacity={deviceAccess.current || userRole === 'admin' ? 0.5 : 1}
              pointerEvents={
                deviceAccess.current || userRole === 'admin' ? 'none' : 'auto'
              }
            >
              <HStack mb={4} justify="flex-start">
                <Checkbox
                  isChecked={deviceConf?.utilization?.status === '1'}
                  onChange={(e) => toggleQuantityBasedStatus(e.target.checked)}
                  // bg="rgba(0, 106, 184, 1)"
                  _checked={{
                    // 1. Set the background color for the box when checked
                    'span:first-of-type': {
                      bg: 'rgba(0, 106, 184, 1)',
                      borderColor: 'rgba(0, 106, 184, 1)', // Ensure border matches background
                      color: 'white', // Ensure the checkmark icon is white
                    },
                  }}
                  size="sm"
                />
                <Text fontWeight="medium">Quantity Based Automation</Text>
              </HStack>
              <VStack
                align="flex-start"
                spacing={2}
                opacity={deviceConf?.utilization?.status === '1' ? 1 : 0.5}
              >
                <Text fontSize="xs" color={textMuted}>
                  Set Quantity (KLD)
                </Text>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={quantityBasedLimit}
                  // value={deviceConf?.utilization?.limit}
                  // onChange={(e) => {
                  //   let value = e.target.value;

                  //   // Allow empty string so user can clear input
                  //   if (value === '') {
                  //     updateQuantityConf('');
                  //     return;
                  //   }

                  //   // Ensure it's a valid integer between 0 and 100
                  //   if (/^\d+$/.test(value)) {
                  //     const intValue = parseInt(value, 10);
                  //     if (intValue >= 0 && intValue <= 100) {
                  //       updateQuantityConf(value); // ✅ keep as string
                  //     }
                  //   }
                  //   // else do nothing → input won't update since it's controlled
                  // }}
                  size="md"
                  maxW="150px"
                  min={0}
                  max={100}
                  isDisabled
                />
              </VStack>
            </Box>

            {/* Level Based Automation */}
            <Box
              p={4}
              border="1px solid"
              borderColor={borderColor}
              bg={insetBg}
              borderRadius="md"
              opacity={deviceAccess.current || userRole === 'admin' ? 0.5 : 1}
              pointerEvents={
                deviceAccess.current || userRole === 'admin' ? 'none' : 'auto'
              }
            >
              <HStack mb={4} justify="flex-start">
                <Checkbox
                  isChecked={deviceConf?.level?.status === '1'}
                  onChange={(e) => toggleLevelBasedStatus(e.target.checked)}
                  _checked={{
                    // 1. Set the background color for the box when checked
                    'span:first-of-type': {
                      bg: 'rgba(0, 106, 184, 1)',
                      borderColor: 'rgba(0, 106, 184, 1)', // Ensure border matches background
                      color: 'white', // Ensure the checkmark icon is white
                    },
                  }}
                  size="sm"
                />
                <Text fontWeight="medium">Level Based Automation</Text>
              </HStack>
              <HStack
                spacing={4}
                opacity={deviceConf?.level?.status === '1' ? 1 : 0.5}
              >
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="xs" color={textMuted}>
                    Low Level %
                  </Text>
                  <Input
                    type="number"
                    value={deviceConf?.level?.tlow}
                    placeholder="Start %"
                    size="md"
                    isDisabled={deviceConf?.level?.status !== '1'}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow empty string so user can clear input
                      if (value === '') {
                        updateLevelConf(['start', '']);
                        return;
                      }

                      // Ensure it's a valid integer between 0 and 100
                      if (/^\d+$/.test(value)) {
                        const intValue = parseInt(value, 10);
                        if (intValue >= 0 && intValue <= 100) {
                          updateLevelConf(['start', value]); // ✅ keep as string
                        }
                      }
                      // else do nothing → input won't update since it's controlled
                    }}
                  />
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="xs" color={textMuted}>
                    High Level %
                  </Text>
                  <Input
                    type="number"
                    value={deviceConf?.level?.thigh}
                    placeholder="Stop %"
                    size="md"
                    isDisabled={deviceConf?.level?.status !== '1'}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow empty string so user can clear input
                      if (value === '') {
                        updateLevelConf(['stop', '']);
                        return;
                      }

                      // Ensure it's a valid integer between 0 and 100
                      if (/^\d+$/.test(value)) {
                        const intValue = parseInt(value, 10);
                        if (intValue >= 0 && intValue <= 100) {
                          updateLevelConf(['stop', value]); // ✅ keep as string
                        }
                      }
                      // else do nothing → input won't update since it's controlled
                    }}
                  />
                </VStack>
              </HStack>
            </Box>
          </SimpleGrid>

          <Divider />

          {/* Schedule Based Automation */}
          <VStack
            align="stretch"
            spacing={4}
            opacity={
              selectedBorewell && !deviceAccess.current && userRole !== 'admin'
                ? 1
                : 0.5
            }
            pointerEvents={
              selectedBorewell && !deviceAccess.current && userRole !== 'admin'
                ? 'auto'
                : 'none'
            }
          >
            <HStack justify="space-between" align="center">
              <Checkbox
                isChecked={deviceConf?.schedules?.status === '1'}
                onChange={(e) => toggleScheduleBasedStatus(e.target.checked)}
                _checked={{
                  // 1. Set the background color for the box when checked
                  'span:first-of-type': {
                    bg: 'rgba(0, 106, 184, 1)',
                    borderColor: 'rgba(0, 106, 184, 1)', // Ensure border matches background
                    color: 'white', // Ensure the checkmark icon is white
                  },
                }}
              >
                Schedule Based Automation
              </Checkbox>
              <Button
                leftIcon={<FaPlus />}
                bg="rgba(0, 106, 184, 1)"
                size="sm"
                color="white"
                rounded="base"
                onClick={handleOpenAddModal} // Open modal for new schedule
                isDisabled={
                  deviceConf?.schedules?.status === '0' || userRole === 'admin'
                }
              >
                ADD Schedule
              </Button>
            </HStack>

            {/* Schedule Table */}
            <Box
              overflowX="auto"
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              sx={{
                // Apply reduced opacity and disable interaction when the schedule is disabled ('0')
                opacity: deviceConf?.schedules?.status === '0' ? 0.6 : 1,
                pointerEvents:
                  deviceConf?.schedules?.status === '0' ? 'none' : 'auto',
              }}
            >
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr bg={insetBg}>
                    <Th w="20px">Sr</Th>
                    <Th w="50px">Enable</Th>
                    <Th w="100px">Start Time</Th>
                    <Th w="100px">Stop Time</Th>
                    <Th>Date</Th>
                    <Th w="80px">Action</Th>
                  </Tr>
                </Thead>
                {selectedBorewell && (
                  <Tbody>
                    {deviceConf?.schedules?.cfg?.map((item, index) => (
                      <Tr key={item.id}>
                        <Td>{index + 1}</Td>
                        <Td>
                          <Switch
                            size="sm"
                            // isChecked is a boolean: item.status === '1'
                            isChecked={item.status === '1'}
                            // REPAIR: Remove isReadOnly to allow interaction
                            // isReadOnly

                            // REPAIR: Add onChange handler
                            onChange={(e) =>
                              handleToggleStatus(index, e.target.checked)
                            }
                            // Using the custom RGBA styling from the previous response
                            sx={{
                              '& .chakra-switch__track': {
                                '--switch-checked-bg': 'rgba(0, 106, 184, 1)',
                              },
                            }}
                          />
                        </Td>
                        <Td>{formatTime12Hour(item.start)}</Td>
                        <Td>{formatTime12Hour(item.stop)}</Td>
                        <Td fontSize="xs">{formatScheduleDays(item.days)}</Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton
                              icon={<FaEdit />}
                              size="xs"
                              variant="ghost"
                              colorScheme="gray"
                              aria-label="Edit"
                              onClick={() => handleOpenEditModal(index)} // Open modal for editing
                            />
                            <IconButton
                              icon={<FaTrashAlt />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              aria-label="Delete"
                              onClick={() => handleDeleteSchedule(index)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                )}
              </Table>
            </Box>
          </VStack>

          <Button
            bg={accentColor}
            size="sm"
            alignSelf="flex-start"
            color="white"
            rounded="base"
            onClick={() => sendConfs()}
            opacity={
              selectedBorewell && !deviceAccess.current && userRole !== 'admin'
                ? 1
                : 0.5
            }
            pointerEvents={
              selectedBorewell && !deviceAccess.current && userRole !== 'admin'
                ? 'auto'
                : 'none'
            }
          >
            Send to Device
          </Button>
        </VStack>
      </VStack>

      {/* --- RIGHT OVERVIEW PANEL --- */}
      <VStack spacing={4} align="stretch">
        {/* OVERVIEW HEADER */}
        <Flex justify="space-between" align="center" pt={0}>
          <Heading size="md" fontWeight="semibold" color={textPrimary}>
            Overview
          </Heading>
          {/* <Button
            leftIcon={<FaDownload />}
            bg="rgba(0, 106, 184, 1)"
            color="white"
            size="sm"
            variant="solid"
            rounded="base"
          >
            Download NOC
          </Button> */}
        </Flex>

        {/* Abstraction Gauges */}
        <Flex
          bg={surfaceBg}
          border="1px solid"
          borderColor={borderColor}
          p={4}
          rounded="2xl"
          shadow={shadowValue}
          gap={1}
          justify="space-around"
          wrap="wrap"
        >
          <Box textAlign="center">
            <Text fontSize="sm" fontWeight="bold">
              Limit - {limitPerDay} m<sup>3</sup>/Day
            </Text>
            <Box
              position="relative"
              display="inline-block"
              w="120px"
              h="120px"
              mx="auto"
            >
              <WaterLimitIcon style={{ width: '120px', height: '120px' }} />
              <Text
                position="absolute"
                top="57%"
                left="52%"
                transform="translate(-50%, -50%)"
                fontSize="xx-small"
                fontWeight="bold"
                textAlign="center"
              >
                {plantDailyCons} m <sup>3</sup>
              </Text>
            </Box>
            <Text fontSize="xs" fontWeight="bold" mt={2}>
              Actual Abstraction
              <br />
              For day
            </Text>
          </Box>

          <Box textAlign="center">
            <Text fontSize="sm" fontWeight="bold">
              Limit - {limitPerYear} m <sup>3</sup>/Year
            </Text>
            <Box
              position="relative"
              display="inline-block"
              w="120px"
              h="120px"
              mx="auto"
            >
              <WaterLimitIcon style={{ width: '120px', height: '120px' }} />
              <Text
                position="absolute"
                top="57%"
                left="52%"
                transform="translate(-50%, -50%)"
                fontSize="xx-small"
                fontWeight="bold"
                textAlign="center"
              >
                {plantYearlyCons} m <sup>3</sup>
              </Text>
            </Box>
            <Text fontSize="xs" fontWeight="bold" mt={2}>
              Actual Abstraction
              <br />
              For Year
            </Text>
          </Box>

          <Box textAlign="center">
            <Box
              position="relative"
              display="inline-block"
              w="120px"
              h="120px"
              mx="auto"
            >
              <TankLevelIcon style={{ width: '120px', height: '120px' }} />
              <Text
                position="absolute"
                top="57%"
                left="52%"
                transform="translate(-50%, -50%)"
                fontSize="lg"
                fontWeight="bold"
                textAlign="center"
                color={accentStrongColor}
              >
                {WaterTankLevel.toFixed(0)}%
              </Text>
            </Box>
            <Text fontSize="xs" fontWeight="bold" mt={2}>
              Overall Tank
              <br />
              Capacity
            </Text>
          </Box>
        </Flex>

        {/* Borewell Status Cards */}
        <VStack spacing={4} align="stretch">
          {overViewData.map((item) => {
            const isRunning = isBorewellRunning(item);

            return (
              <BorewellCard
                key={item.productId}
                title={item.borewellName}
                statusColor={getBorewellStatusColor(item)}
                isPumpActive={isRunning}
              />
            );
          })}
        </VStack>
      </VStack>
    </Grid>
  );
};
export default DashboardUI;
